import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import type { PartialOptions } from 'overlayscrollbars';
import { api, type Reader, type ReaderFilesSummary, type ReaderPersonNotesSummary } from '@/api/client';
import { CellCommentsTableProvider } from '@/features/project-cell-comments/CellCommentsTableContext';
import { ReaderNotesPopover } from '@/components/ReaderNotesPopover/ReaderNotesPopover';
import { ReaderFilesPopover } from '@/components/ReaderFilesPopover/ReaderFilesPopover';
import { ChoiceDialog } from '@/components/ui/ChoiceDialog/ChoiceDialog';
import { Button } from '@/components/ui/Button/Button';
import { ReadersTableRow } from '@/components/ReadersTable/ReadersTableRow';
import { getReadersGridColumns, getVisibleReaderColumns } from '@/components/ReadersTable/readersTableLayout';
import { useAuth } from '@/contexts/useAuth';
import { getPersonNotesQueryOptions } from '@/components/ProjectNotesPopover/personNotesPopoverQuery';
import { getReaderFilesQueryOptions } from '@/components/ReaderFilesPopover/readerFilesPopoverQuery';
import { useToast } from '@/contexts/ToastContext';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { getTableRowHeightPx } from '@/utils/cssTokens';
import { measureTableVirtualRowElement } from '@/utils/tableVirtualMeasure';
import {
  READERS_TABLE_QUERY_KEY,
  READERS_CATALOG_LIST_PAGE_SIZE,
  readersTableInfiniteQueryKey,
} from '@/lib/readersCatalogQueryCache';
import { readerCatalogTableDisplayName } from '@/utils/readerCatalogTableDisplayName';
import { useAnimatedPopover } from '@/hooks/useAnimatedPopover';
import { useReadersCatalogFilters } from '@/contexts/ReadersCatalogFiltersContext';
import { useReadersCatalogSearch } from '@/contexts/ReadersCatalogSearchContext';
import { ReadersCatalogHeaderFilterCell } from '@/components/ReadersTable/ReadersCatalogHeaderFilterCell';
import { parseSearchHighlightWords } from '@/utils/highlightSearchTerms';
import '@/components/ProjectsTable/ProjectsTable.css';
import './ReadersTable.css';

const READERS_FILTERABLE_COLUMN_IDS = new Set([
  'state',
  'name',
  'studio',
  'legal_entity',
  'reader_price',
  'tags',
]);

/** Та же тема скроллбаров, что у модалки; обе оси — иначе широкая таблица обрежется (базовый TRACKER задаёт overflow-x: hidden). */
const READERS_TABLE_SCROLL_OS_OPTIONS: PartialOptions = {
  overflow: { x: 'scroll', y: 'scroll' },
};

type DeleteDialogState =
  | { mode: 'reader-only'; personId: string; name: string }
  | { mode: 'multi-role'; personId: string; name: string; otherRoles: string[] };

export interface ReadersTableProps {
  onOpenPersonManage?: (personId: string) => void;
}

export function ReadersTable({ onOpenPersonManage }: ReadersTableProps) {
  const queryClient = useQueryClient();
  const { capabilities } = useAuth();
  const showReaderPrice = capabilities.prices;
  const visibleColumns = useMemo(() => getVisibleReaderColumns(showReaderPrice), [showReaderPrice]);
  const gridColumns = useMemo(() => getReadersGridColumns(showReaderPrice), [showReaderPrice]);
  const { showSuccess, showError } = useToast();
  const { debouncedSearch } = useReadersCatalogSearch();
  const viewportRef = useRef<HTMLElement | null>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(44);
  const [notesOpeningId, setNotesOpeningId] = useState<string | null>(null);
  const [filesOpeningId, setFilesOpeningId] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
  const [deleteContextLoadingId, setDeleteContextLoadingId] = useState<string | null>(null);
  const [dialogSubmitting, setDialogSubmitting] = useState(false);

  const readersInfinite = useInfiniteQuery({
    queryKey: readersTableInfiniteQueryKey(debouncedSearch),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const q = debouncedSearch.trim() ? debouncedSearch.trim() : undefined;
      const raw = await api.readers.list(q, {
        limit: READERS_CATALOG_LIST_PAGE_SIZE,
        offset: pageParam,
      });
      const items = Array.isArray(raw) ? raw : raw.items;
      const hasMore = Array.isArray(raw) ? false : raw.hasMore;
      return {
        items,
        hasMore,
        nextOffset: pageParam + items.length,
      };
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextOffset : undefined),
  });

  const readers = useMemo(
    () => readersInfinite.data?.pages.flatMap((p) => p.items) ?? [],
    [readersInfinite.data]
  );

  const searchHighlightWords = useMemo(
    () => (debouncedSearch.trim() ? parseSearchHighlightWords(debouncedSearch) : []),
    [debouncedSearch]
  );

  const readerIds = useMemo(() => readers.map((reader) => reader.id), [readers]);

  const summariesQuery = useQuery({
    queryKey: ['readersTableSummaries', readerIds],
    queryFn: async () => {
      const [cell, notes, files] = await Promise.all([
        api.readers.cellCommentSummaries(readerIds),
        api.readers.personNotesSummaries(readerIds),
        api.readers.fileSummaries(readerIds),
      ]);
      return {
        cellSummaries: cell.summaries,
        personNotesSummaries: notes.summaries,
        readerFilesSummaries: files.summaries,
      };
    },
    enabled: readerIds.length > 0,
  });

  const { filterReaders } = useReadersCatalogFilters();
  const filteredReaders = useMemo(() => filterReaders(readers), [filterReaders, readers]);

  const getReader = useCallback((id: string) => readers.find((r) => r.id === id), [readers]);

  const notesPopover = useAnimatedPopover<{ reader: Reader; anchorEl: HTMLElement }>((item) => item.reader.id);
  const filesPopover = useAnimatedPopover<{ reader: Reader; anchorEl: HTMLElement }>((item) => item.reader.id);

  /** Без нового `{}` каждый рендер пока данных нет — стабильные пропсы для memo строк. */
  const emptyCellCommentSummaries = useMemo(
    (): Record<string, Record<string, number>> => ({}),
    []
  );
  const emptyPersonNotesSummariesMap = useMemo(
    (): Record<string, ReaderPersonNotesSummary> => ({}),
    []
  );
  const emptyReaderFilesSummariesMap = useMemo(
    (): Record<string, ReaderFilesSummary> => ({}),
    []
  );
  const summaries = summariesQuery.data?.cellSummaries ?? emptyCellCommentSummaries;
  const personNotesSummaries =
    summariesQuery.data?.personNotesSummaries ?? emptyPersonNotesSummariesMap;
  const readerFilesSummaries =
    summariesQuery.data?.readerFilesSummaries ?? emptyReaderFilesSummariesMap;
  const notesSummaryLoading =
    readerIds.length > 0 && !summariesQuery.data && (summariesQuery.isFetching || summariesQuery.isPending);

  useLayoutEffect(() => {
    const el = headerRowRef.current;
    if (!el) return;
    const sync = () => setHeaderHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    sync();
    return () => ro.disconnect();
  }, [filteredReaders.length]);

  const getReaderItemKey = useCallback(
    (index: number) => filteredReaders[index]?.id ?? String(index),
    [filteredReaders]
  );

  const rowVirtualizer = useVirtualizer({
    count: filteredReaders.length,
    getScrollElement: () => viewportRef.current,
    estimateSize: () => getTableRowHeightPx(),
    overscan: 10,
    paddingStart: headerHeight,
    measureElement: measureTableVirtualRowElement,
    getItemKey: getReaderItemKey,
    /** Согласование с OverlayScrollBars: RO в следующем кадре, меньше «нулевых» первых измерений. */
    useAnimationFrameWithResizeObserver: true,
  });

  const rowVirtualizerRef = useRef(rowVirtualizer);
  rowVirtualizerRef.current = rowVirtualizer;
  const measureRef = useCallback((el: HTMLDivElement | null) => {
    if (el) rowVirtualizerRef.current.measureElement(el);
  }, []);

  const totalHeight = filteredReaders.length > 0 ? rowVirtualizer.getTotalSize() : 0;
  const virtualItems = filteredReaders.length > 0 ? rowVirtualizer.getVirtualItems() : [];

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(null);
    setDialogSubmitting(false);
  }, []);

  const invalidateReadersQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: READERS_TABLE_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ['readersTableSummaries'] });
  }, [queryClient]);

  const handleRequestDelete = useCallback(
    async (reader: Reader) => {
      setDeleteContextLoadingId(reader.id);
      try {
        const ctx = await api.readers.deletionContext(reader.id);
        const displayName = readerCatalogTableDisplayName(reader);
        if (ctx.otherRoles.length === 0) {
          setDeleteDialog({ mode: 'reader-only', personId: reader.id, name: displayName });
        } else {
          setDeleteDialog({
            mode: 'multi-role',
            personId: reader.id,
            name: displayName,
            otherRoles: ctx.otherRoles,
          });
        }
      } catch (err) {
        showError((err as Error).message || 'Не удалось подготовить удаление');
      } finally {
        setDeleteContextLoadingId(null);
      }
    },
    [showError]
  );

  const runDeleteReaderRole = useCallback(async () => {
    if (!deleteDialog) return;
    const { personId } = deleteDialog;
    setDialogSubmitting(true);
    try {
      await api.readers.delete(personId);
      showSuccess('Чтец удалён из каталога');
      closeDeleteDialog();
      await invalidateReadersQueries();
    } catch (err) {
      showError((err as Error).message || 'Не удалось удалить чтеца');
    } finally {
      setDialogSubmitting(false);
    }
  }, [deleteDialog, showSuccess, showError, closeDeleteDialog, invalidateReadersQueries]);

  const runDeletePerson = useCallback(async () => {
    if (!deleteDialog) return;
    const { personId } = deleteDialog;
    setDialogSubmitting(true);
    try {
      await api.persons.delete(personId);
      showSuccess('Персона удалена из базы');
      closeDeleteDialog();
      await invalidateReadersQueries();
    } catch (err) {
      showError((err as Error).message || 'Не удалось удалить персону');
    } finally {
      setDialogSubmitting(false);
    }
  }, [deleteDialog, showSuccess, showError, closeDeleteDialog, invalidateReadersQueries]);

  const onOpenNotes = useCallback(
    async (reader: Reader, anchorEl: HTMLElement) => {
      setNotesOpeningId(reader.id);
      try {
        await queryClient.fetchQuery(getPersonNotesQueryOptions(reader.id));
        notesPopover.open({ reader, anchorEl });
      } catch (err) {
        showError((err as Error).message || 'Не удалось загрузить заметки');
      } finally {
        setNotesOpeningId(null);
      }
    },
    [notesPopover.open, queryClient, showError]
  );

  const onOpenFiles = useCallback(
    async (reader: Reader, anchorEl: HTMLElement) => {
      setFilesOpeningId(reader.id);
      try {
        await queryClient.fetchQuery(getReaderFilesQueryOptions(reader.id));
        filesPopover.open({ reader, anchorEl });
      } catch (err) {
        showError((err as Error).message || 'Не удалось загрузить файлы');
      } finally {
        setFilesOpeningId(null);
      }
    },
    [filesPopover.open, queryClient, showError]
  );

  const headerRow = (
    <div ref={headerRowRef} className="projects-table__header-row" style={{ gridTemplateColumns: gridColumns }}>
      {visibleColumns.map((column, index) => (
        <div
          key={column.id}
          className={clsx(
            'projects-table__header-cell',
            index === visibleColumns.length - 1 && 'projects-table__header-cell_last'
          )}
          role="columnheader"
        >
          {READERS_FILTERABLE_COLUMN_IDS.has(column.id) ? (
            <ReadersCatalogHeaderFilterCell
              columnId={column.id}
              columnTitle={column.title}
              readers={readers}
            />
          ) : (
            <span className="projects-table__header-cell-text">{column.title}</span>
          )}
        </div>
      ))}
    </div>
  );

  const deleteDialogDescription =
    deleteDialog?.mode === 'reader-only' ? (
      <>
        <strong>{deleteDialog.name}</strong> есть только в каталоге чтецов. Персона будет удалена из базы целиком. Это
        действие необратимо.
      </>
    ) : deleteDialog?.mode === 'multi-role' ? (
      <>
        <strong>{deleteDialog.name}</strong> встречается ещё как {deleteDialog.otherRoles.join(', ')}. Очистить{' '}
        <strong>{deleteDialog.name}</strong> только из списка чтецов или из базы полностью, включая другие роли?
      </>
    ) : null;

  const deleteDialogActions =
    deleteDialog?.mode === 'reader-only'
      ? [
          {
            label: 'Удалить',
            variant: 'dangerOutline' as const,
            disabled: dialogSubmitting,
            onClick: () => void runDeletePerson(),
          },
        ]
      : deleteDialog?.mode === 'multi-role'
        ? [
            {
              label: 'Удалить из списка чтецов',
              variant: 'outlined' as const,
              disabled: dialogSubmitting,
              onClick: () => void runDeleteReaderRole(),
            },
            {
              label: 'Удалить полностью',
              variant: 'dangerOutline' as const,
              disabled: dialogSubmitting,
              onClick: () => void runDeletePerson(),
            },
          ]
        : [];

  const tableListLoading = readersInfinite.isPending && !readersInfinite.data;
  const tableListError = readersInfinite.isError;
  const searchActive = debouncedSearch.trim().length > 0;

  const showVirtualizedRows =
    !tableListLoading && !tableListError && readers.length > 0 && filteredReaders.length > 0;

  return (
    <CellCommentsTableProvider>
      <div className="projects-table readers-table-catalog" role="table" aria-label="Чтецы">
        <OverlayScrollArea
          className="projects-table__scroll"
          defer={false}
          osOptions={READERS_TABLE_SCROLL_OS_OPTIONS}
          viewportRef={viewportRef}
        >
          <div
            className="projects-table__inner"
            style={showVirtualizedRows ? { height: `${totalHeight}px` } : undefined}
          >
            {headerRow}

            {tableListLoading ? (
              <div className="projects-table__loading" aria-busy="true">
                Загрузка таблицы...
              </div>
            ) : tableListError ? (
              <div className="projects-table__empty" role="alert" style={{ color: 'var(--accent-error)' }}>
                Не удалось загрузить чтецов
              </div>
            ) : readers.length === 0 ? (
              <div className="projects-table__empty" role="status">
                {searchActive ? 'Ничего не найдено' : 'Нет чтецов'}
              </div>
            ) : filteredReaders.length === 0 ? (
              <div className="projects-table__empty">Нет строк по заданным фильтрам</div>
            ) : (
              virtualItems.map((virtualRow) => {
                const reader = filteredReaders[virtualRow.index];
                if (!reader) return null;
                const rowSummary = summaries[reader.id];
                const rowNotesSummary = personNotesSummaries[reader.id];
                const rowFilesSummary = readerFilesSummaries[reader.id];
                const rowBusy =
                  deleteContextLoadingId === reader.id ||
                  (dialogSubmitting && deleteDialog?.personId === reader.id);

                return (
                  <ReadersTableRow
                    key={virtualRow.key}
                    reader={reader}
                    searchHighlightWords={searchHighlightWords}
                    cellCommentSummary={rowSummary}
                    personNotesSummary={rowNotesSummary}
                    notesSummaryLoading={notesSummaryLoading}
                    notesOpening={notesOpeningId === reader.id}
                    readerFilesSummary={rowFilesSummary}
                    filesOpening={filesOpeningId === reader.id}
                    rowBusy={rowBusy}
                    top={virtualRow.start}
                    dataIndex={virtualRow.index}
                    measureRef={measureRef}
                    gridTemplateColumns={gridColumns}
                    showReaderPrice={showReaderPrice}
                    onRequestDelete={handleRequestDelete}
                    onOpenNotes={onOpenNotes}
                    onOpenFiles={onOpenFiles}
                    onOpenPersonManage={onOpenPersonManage}
                  />
                );
              })
            )}
          </div>
        </OverlayScrollArea>

        {readersInfinite.hasNextPage ? (
          <div className="readers-table-catalog__search-more">
            <Button
              type="button"
              variant="secondary"
              size="m"
              disabled={readersInfinite.isFetchingNextPage}
              onClick={() => void readersInfinite.fetchNextPage()}
            >
              {readersInfinite.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
            </Button>
          </div>
        ) : null}

        {notesPopover.exiting && (
          <ReaderNotesPopover
            key={`notes-${notesPopover.exiting.reader.id}`}
            open={false}
            onClose={notesPopover.clearExiting}
            anchorEl={notesPopover.exiting.anchorEl}
            readerId={notesPopover.exiting.reader.id}
            readerName={readerCatalogTableDisplayName(
              getReader(notesPopover.exiting.reader.id) ?? notesPopover.exiting.reader
            )}
          />
        )}
        {notesPopover.active && (
          <ReaderNotesPopover
            key={`notes-${notesPopover.active.reader.id}`}
            open
            onClose={notesPopover.close}
            anchorEl={notesPopover.active.anchorEl}
            readerId={notesPopover.active.reader.id}
            readerName={readerCatalogTableDisplayName(
              getReader(notesPopover.active.reader.id) ?? notesPopover.active.reader
            )}
          />
        )}
        {filesPopover.exiting && (
          <ReaderFilesPopover
            key={`files-${filesPopover.exiting.reader.id}`}
            open={false}
            onClose={filesPopover.clearExiting}
            anchorEl={filesPopover.exiting.anchorEl}
            readerId={filesPopover.exiting.reader.id}
            readerName={readerCatalogTableDisplayName(
              getReader(filesPopover.exiting.reader.id) ?? filesPopover.exiting.reader
            )}
          />
        )}
        {filesPopover.active && (
          <ReaderFilesPopover
            key={`files-${filesPopover.active.reader.id}`}
            open
            onClose={filesPopover.close}
            anchorEl={filesPopover.active.anchorEl}
            readerId={filesPopover.active.reader.id}
            readerName={readerCatalogTableDisplayName(
              getReader(filesPopover.active.reader.id) ?? filesPopover.active.reader
            )}
          />
        )}

        <ChoiceDialog
          open={deleteDialog !== null}
          onClose={() => {
            if (dialogSubmitting) return;
            closeDeleteDialog();
          }}
          title="Удаление"
          description={deleteDialogDescription}
          actions={deleteDialogActions}
        />
      </div>
    </CellCommentsTableProvider>
  );
}
